import { approxGTE, approxLTE } from '@/player-config/src/utils/compareUtils';
import { Word } from '@/player-config/src/types/transcript-types';

const SENTENCE_END_PUNCTUATION = /[.!?]$/;

function isSentenceEnd(word: Word): boolean {
  return SENTENCE_END_PUNCTUATION.test(word.content.trim());
}

function getSentenceRanges(words: Word[]): { start: number; end: number }[] {
  if (words.length === 0) {
    return [];
  }
  const ranges: { start: number; end: number }[] = [];
  let sentenceStart = words[0].start_time;
  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    if (isSentenceEnd(word)) {
      ranges.push({ start: sentenceStart, end: word.end_time });
      if (i + 1 < words.length) {
        sentenceStart = words[i + 1].start_time;
      }
    }
  }
  if (ranges.length === 0) {
    ranges.push({
      start: words[0].start_time,
      end: words[words.length - 1].end_time
    });
  }
  return ranges;
}

/**
 * Snaps a cue point (in segment time) to the nearest sentence boundary using a 50% rule:
 * - If cue is >= 50% through the sentence duration → snap to end of sentence
 * - If cue is < 50% through the sentence duration → snap to start of sentence
 * Returns cueSegmentTime unchanged if no sentence contains the cue point.
 */
export function snapCuePointToSentenceBoundary(cueSegmentTime: number, words: Word[]): number {
  const ranges = getSentenceRanges(words);
  const sentence = ranges.find(r => approxGTE(cueSegmentTime, r.start) && approxLTE(cueSegmentTime, r.end));
  if (!sentence) {
    return cueSegmentTime;
  }
  const duration = sentence.end - sentence.start;
  if (duration <= 0) {
    return sentence.end;
  }
  const position = cueSegmentTime - sentence.start;
  return position / duration >= 0.5 ? sentence.end : sentence.start;
}
